import { useState, useEffect } from "react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import PageHeading from "../PageHeading";
import ExportOptions from "../ExportOptions";
import DynamicSelect from "../DynamicSelect";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Pagination from "../Shared/Pagination";

const API_BASE = "https://api.muktihospital.com/api/pathology-accounts/account-summary";
const API_KEY =
  process.env.REACT_APP_API_KEY ||
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const money = (n) => (Number(n) || 0).toFixed(2);

const PathologyAccounts = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [filterType, setFilterType] = useState("daily");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const limit = 10;

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Accounts" },
    { label: "Pathology Accounts" },
  ];

  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const headers = [
    "Sl", "Period", "Bills", "Income", "Agent", "Doctor",
    "Performer", "Others", "Total Expense", "Net Profit",
  ];

  const dataMapper = (() => {
    let counter = 0;
    return (r) => {
      counter += 1;
      return [
        counter, r.label, r.totalBills,
        money(r.income), money(r.agent), money(r.doctor),
        money(r.performer), money(r.others), money(r.expense), money(r.profit),
      ].join("\t");
    };
  })();

  // Fetch a single account-summary for one sub-period and map it to a row
  const fetchOne = async (params, label) => {
    try {
      const res = await fetch(`${API_BASE}?${params.toString()}`, {
        method: "GET",
        headers: { "Content-Type": "application/json", "x-api-key": API_KEY },
      });
      const data = await res.json();
      const s = (res.ok && data.summary) ? data.summary : {};
      const agent = Number(s.totalAgentCommission) || 0;
      const doctor = Number(s.totalDoctorPaid) || 0;
      const performer = Number(s.totalPerformerPaid) || 0;
      const others = Number(s.totalOthersExpense) || 0;
      return {
        id: label,
        label,
        totalBills: Number(s.totalBills) || 0,
        income: Number(s.totalCollected) || 0,
        agent, doctor, performer, others,
        expense: agent + doctor + performer + others,
        profit: Number(s.netProfit) || 0,
      };
    } catch (err) {
      console.error("Row fetch error:", err);
      return { id: label, label, totalBills: 0, income: 0, agent: 0, doctor: 0, performer: 0, others: 0, expense: 0, profit: 0 };
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    try {
      let jobs = [];

      if (filterType === "daily") {
        // One row per day in the selected range
        const start = new Date(startDate);
        const end = new Date(endDate);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const day = new Date(d).toISOString().split("T")[0];
          const params = new URLSearchParams({ type: "daily", startDate: day, endDate: day });
          const label = new Date(day).toLocaleDateString();
          jobs.push(fetchOne(params, label));
        }
      } else if (filterType === "monthly") {
        // One row per month of the selected year (Jan–Dec)
        const [year] = selectedMonth.split("-");
        for (let m = 1; m <= 12; m++) {
          const params = new URLSearchParams({ type: "monthly", year, month: String(m) });
          const label = new Date(year, m - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
          jobs.push(fetchOne(params, label));
        }
      } else {
        // Yearly: one row for the selected year
        const params = new URLSearchParams({ type: "yearly", year: selectedYear });
        jobs.push(fetchOne(params, selectedYear));
      }

      const result = await Promise.all(jobs);
      setRows(result);
      setCurrentPage(1);
    } catch (err) {
      setError("Unable to connect to the server. Please try again later.");
      console.error("Fetch report error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, startDate, endDate, selectedMonth, selectedYear]);

  // Client-side pagination
  const totalPages = Math.ceil(rows.length / limit) || 1;
  const pagedRows = rows.slice((currentPage - 1) * limit, currentPage * limit);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  // Grand totals (all rows, not just current page)
  const totals = rows.reduce(
    (a, r) => ({
      bills: a.bills + r.totalBills,
      income: a.income + r.income,
      agent: a.agent + r.agent,
      doctor: a.doctor + r.doctor,
      performer: a.performer + r.performer,
      others: a.others + r.others,
      expense: a.expense + r.expense,
      profit: a.profit + r.profit,
    }),
    { bills: 0, income: 0, agent: 0, doctor: 0, performer: 0, others: 0, expense: 0, profit: 0 }
  );


  return (
    <div>
      <PageHeading title="Pathology Accounts" breadcrumbs={breadcrumbs} />
      <div className="bg-white shadow-sm rounded-lg">
        {/* Filter bar */}
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
         <div className="inline-flex items-center gap-3 shrink-0"><h2 className="text-base font-medium text-gray-700">Pathology Accounts Report</h2></div>
          <div className="flex items-center gap-2">
            <label className="label mb-0 shrink-0">Filter By:</label>
            <div className="w-32 shrink-0">
              <DynamicSelect
                options={filterOptions}
                onChange={(option) => setFilterType(option.value)}
                value={filterOptions.find((opt) => opt.value === filterType)}
                isSearchable={false}
                controlBackgroundColor="#f7fafc"
                controlBorderColor="#cbd5e0"
              />
            </div>
            {filterType === "daily" && (
              <>
                <label className="label mb-0 shrink-0">From:</label>
                <input type="date" className="input-field" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                <label className="label mb-0 shrink-0">To:</label>
                <input type="date" className="input-field" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </>
            )}
            {filterType === "monthly" && (
              <input type="month" className="input-field" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
            )}
            {filterType === "yearly" && (
              <input type="number" className="input-field w-28" value={selectedYear} min="2000" max="2099" onChange={(e) => setSelectedYear(e.target.value)} />
            )}
            <ExportOptions
              data={rows}
              title="Pathology Accounts Report"
              headers={headers}
              dataMapper={dataMapper}
              dropdownOpen={dropdownOpen}
              toggleDropdown={() => setDropdownOpen((p) => !p)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full divide-y divide-gray-200 print-table">
            <thead>
              <tr className="bg-[#FBFBFD]">
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Sl</th>
                <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  {filterType === "daily" ? "Date" : filterType === "monthly" ? "Month" : "Year"}
                </th>
                <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Bills</th>
                <th className="text-right uppercase font-inter font-medium text-sm text-green-700 p-3">Income</th>
                <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Agent</th>
                <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Doctor</th>
                <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Performer</th>
                <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Others</th>
                <th className="text-right uppercase font-inter font-medium text-sm text-red-700 p-3">Expense</th>
                <th className="text-right uppercase font-inter font-medium text-sm text-blue-700 p-3">Profit</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="10" className="text-center p-6 text-gray-500">Loading report...</td></tr>
              ) : error ? (
                <tr><td colSpan="10" className="text-center p-4 text-red-500">{error}</td></tr>
              ) : pagedRows.length > 0 ? (
                pagedRows.map((r, index) => (
                  <tr key={r.id} className="hover:bg-gray-50 border-b">
                    <td className="text-left font-inter text-sm text-M-heading-color p-3">{(currentPage - 1) * limit + index + 1}</td>
                    <td className="text-left font-inter text-sm text-M-heading-color p-3">{r.label}</td>
                    <td className="text-right font-inter text-sm text-M-heading-color p-3">{r.totalBills}</td>
                    <td className="text-right font-inter text-sm text-green-600 p-3">{money(r.income)}</td>
                    <td className="text-right font-inter text-sm text-M-heading-color p-3">{money(r.agent)}</td>
                    <td className="text-right font-inter text-sm text-M-heading-color p-3">{money(r.doctor)}</td>
                    <td className="text-right font-inter text-sm text-M-heading-color p-3">{money(r.performer)}</td>
                    <td className="text-right font-inter text-sm text-M-heading-color p-3">{money(r.others)}</td>
                    <td className="text-right font-inter text-sm text-red-600 p-3">{money(r.expense)}</td>
                    <td className="text-right font-inter text-sm font-semibold text-blue-700 p-3">{money(r.profit)}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="10" className="text-center p-4 text-gray-500">No pathology data found</td></tr>
              )}

              {!loading && !error && rows.length > 0 && (
                <tr className="border-t-2 border-M-text-color/30 bg-gray-50 font-bold">
                  <td className="text-right font-inter text-sm text-M-heading-color p-3" colSpan={2}>Total:</td>
                  <td className="text-right font-inter text-sm text-M-heading-color p-3">{totals.bills}</td>
                  <td className="text-right font-inter text-sm text-green-700 p-3">{money(totals.income)}</td>
                  <td className="text-right font-inter text-sm text-M-heading-color p-3">{money(totals.agent)}</td>
                  <td className="text-right font-inter text-sm text-M-heading-color p-3">{money(totals.doctor)}</td>
                  <td className="text-right font-inter text-sm text-M-heading-color p-3">{money(totals.performer)}</td>
                  <td className="text-right font-inter text-sm text-M-heading-color p-3">{money(totals.others)}</td>
                  <td className="text-right font-inter text-sm text-red-700 p-3">{money(totals.expense)}</td>
                  <td className="text-right font-inter text-sm text-blue-700 p-3">{money(totals.profit)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={rows.length}
          itemsPerPage={limit}
          onPageChange={handlePageChange}
        />
        <ToastContainer />
      </div>
    </div>
  );
};

export default PathologyAccounts;